import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useState, useEffect, useMemo } from 'react';
import useFetch from 'use-http';
import { buildLeaseLesseeOptions, ILeaseCompanyOptionSource, ILeaseLesseeOption } from './leaseLesseeOptions';
import useLatestRequest from '../useLatestRequest';

interface ILesseePickerModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (lessee: string) => Promise<boolean>;
    currentValue?: string;
    clientNumber?: number;
}

interface IClientLookup {
    name?: string;
}

// SVG Icons
const SvgIcon: React.FC<React.SVGProps<SVGSVGElement> & { className?: string }> = ({ children, className, ...props }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        {...props}
    >
        {children}
    </svg>
);

const X: React.FC<React.SVGProps<SVGSVGElement> & { className?: string }> = (props) => (
    <SvgIcon {...props}>
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
    </SvgIcon>
);

const Search: React.FC<React.SVGProps<SVGSVGElement> & { className?: string }> = (props) => (
    <SvgIcon {...props}>
        <circle cx="11" cy="11" r="8" />
        <path d="m21 21-4.3-4.3" />
    </SvgIcon>
);

const Loader2: React.FC<React.SVGProps<SVGSVGElement> & { className?: string }> = (props) => (
    <SvgIcon {...props}>
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </SvgIcon>
);

const Building2: React.FC<React.SVGProps<SVGSVGElement> & { className?: string }> = (props) => (
    <SvgIcon {...props}>
        <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
        <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
        <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
        <path d="M10 6h4" />
        <path d="M10 10h4" />
        <path d="M10 14h4" />
        <path d="M10 18h4" />
    </SvgIcon>
);

const LesseePickerModal: React.FC<ILesseePickerModalProps> = ({ isOpen, onClose, onSelect, currentValue, clientNumber }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [lesseeOptions, setLesseeOptions] = useState<ILeaseLesseeOption[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [savingLessee, setSavingLessee] = useState<string | null>(null);

    const { get } = useFetch();
    const loadRequests = useLatestRequest();
    const selectRequests = useLatestRequest();

    // Load the same base lessee choices used by lease 1099 company selection, plus the current lease client.
    useEffect(() => {
        if (!isOpen) {
            loadRequests.invalidate();
            selectRequests.invalidate();
            setLesseeOptions([]);
            setSearchTerm('');
            return undefined;
        }

        const fetchLessees = async () => {
            const requestId = loadRequests.begin();
            setLoading(true);
            setError(null);

            try {
                const [companiesResponse, clientResponse] = await Promise.all([
                    get('/v1.0/leases/master/companies'),
                    clientNumber ? get(`/v1.0/clients/${clientNumber}`) : Promise.resolve(undefined),
                ]);
                if (!loadRequests.isCurrent(requestId)) {
                    return;
                }

                if (Array.isArray(companiesResponse)) {
                    setLesseeOptions(
                        buildLeaseLesseeOptions(companiesResponse as ILeaseCompanyOptionSource[], (clientResponse as IClientLookup | undefined)?.name)
                    );
                } else {
                    throw new Error('Failed to fetch lessee options');
                }
            } catch (e) {
                if (!loadRequests.isCurrent(requestId)) {
                    return;
                }
                setError('Failed to retrieve lessees from the server.');
                setLesseeOptions([]);
            } finally {
                if (loadRequests.isCurrent(requestId)) {
                    setLoading(false);
                }
            }
        };

        fetchLessees();
        return () => loadRequests.invalidate();
    }, [clientNumber, isOpen, get, loadRequests, selectRequests]);

    // Filter lessees based on search term (client-side filtering for already loaded data)
    const filteredLessees = useMemo(() => {
        if (!searchTerm) {
            return lesseeOptions;
        }
        const lowerCaseSearch = searchTerm.toLowerCase();
        return lesseeOptions.filter((lessee) => lessee.label.toLowerCase().includes(lowerCaseSearch));
    }, [lesseeOptions, searchTerm]);

    const handleSelect = async (lessee: ILeaseLesseeOption): Promise<void> => {
        const requestId = selectRequests.begin();
        setSavingLessee(lessee.value);
        const wasSaved = await onSelect(lessee.label);
        if (!selectRequests.isCurrent(requestId)) {
            return;
        }
        setSavingLessee(null);
        if (wasSaved) {
            onClose();
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={styles.modalOverlay} onClick={onClose}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div style={styles.modalHeader}>
                    <h2 style={styles.modalTitle}>
                        <Building2 style={styles.headerIcon} />
                        Select Lessee
                    </h2>
                    <button onClick={onClose} style={styles.closeButton} aria-label="Close dialog">
                        <X style={styles.closeIcon} />
                    </button>
                </div>

                {/* Body Content */}
                <div style={styles.modalBody}>
                    {/* Search Bar */}
                    <div style={styles.searchContainer}>
                        <div style={styles.searchInputWrapper}>
                            <Search style={styles.searchIcon} />
                            <LeaseRecordInput
                                type="text"
                                placeholder="Search lessees..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={styles.inputBase}
                                autoFocus={true}
                            />
                        </div>
                        {currentValue && (
                            <div style={styles.currentValueNote}>
                                Current: <strong>{currentValue}</strong>
                            </div>
                        )}
                    </div>

                    {/* Loading/Error/Empty State */}
                    {loading && (
                        <div style={styles.loadingState}>
                            <Loader2 style={styles.loaderIcon} />
                            <span style={styles.loadingText}>Loading lessees...</span>
                        </div>
                    )}

                    {error && <div style={styles.errorState}>Error: {error}</div>}

                    {!loading && !error && filteredLessees.length === 0 && (
                        <div style={styles.emptyState}>
                            <Search style={styles.emptyIcon} />
                            <span style={styles.emptyText}>{searchTerm ? `No lessees found matching "${searchTerm}"` : 'No lessees found'}</span>
                        </div>
                    )}

                    {/* Lessees List */}
                    {!loading && !error && filteredLessees.length > 0 && (
                        <div style={styles.listWrapper}>
                            {filteredLessees.map((lessee) => (
                                <div
                                    key={lessee.value}
                                    style={{
                                        ...styles.listItem,
                                        backgroundColor: lessee.label === currentValue ? colors.blue50 : '#fff',
                                        opacity: savingLessee && savingLessee !== lessee.value ? 0.55 : 1,
                                        pointerEvents: savingLessee ? 'none' : 'auto',
                                    }}
                                    onClick={() => void handleSelect(lessee)}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor = colors.blue50;
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = lessee.label === currentValue ? colors.blue50 : '#fff';
                                    }}
                                >
                                    <Building2 style={styles.listItemIcon} />
                                    <span style={styles.listItemText}>{savingLessee === lessee.value ? 'Saving...' : lessee.label}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div style={styles.modalFooter}>
                    <button onClick={onClose} style={styles.buttonSecondary}>
                        Cancel
                    </button>
                </div>
            </div>
        </div>
    );
};

// Component Styles
const colors = {
    blue50: '#EFF6FF',
    blue500: '#3B82F6',
    blue600: '#2563EB',
    gray50: '#F9FAFB',
    gray100: '#F3F4F6',
    gray200: '#E5E7EB',
    gray300: '#D1D5DB',
    gray400: '#9CA3AF',
    gray500: '#6B7280',
    gray600: '#4B5563',
    gray700: '#374151',
    gray800: '#1F2937',
    gray900: '#111827',
    red50: '#FEF2F2',
    red600: '#DC2626',
};

const styles: { [key: string]: React.CSSProperties } = {
    // Modal Structure
    modalOverlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.75)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 50,
        padding: '1rem',
        backdropFilter: 'blur(1px)',
    },
    modalContent: {
        backgroundColor: '#FFFFFF',
        borderRadius: '1rem',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        width: '90%',
        maxWidth: '600px',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '1.5rem',
        borderBottom: `1px solid ${colors.gray200}`,
    },
    modalTitle: {
        fontSize: '1.5rem',
        fontWeight: 700,
        color: colors.gray900,
        display: 'flex',
        alignItems: 'center',
        margin: 0,
    },
    headerIcon: {
        width: '1.5rem',
        height: '1.5rem',
        marginRight: '0.5rem',
        color: colors.blue600,
    },
    closeButton: {
        color: colors.gray400,
        padding: '0.25rem',
        borderRadius: '50%',
        backgroundColor: 'transparent',
        border: 'none',
        cursor: 'pointer',
        transition: 'all 0.15s ease-in-out',
    },
    closeIcon: {
        width: '1.5rem',
        height: '1.5rem',
    },
    modalBody: {
        padding: '1.5rem',
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
        overflow: 'hidden',
        gap: '1rem',
    },
    modalFooter: {
        padding: '1rem',
        borderTop: `1px solid ${colors.gray200}`,
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.75rem',
    },

    // Search Bar
    searchContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
    },
    searchInputWrapper: { position: 'relative' },
    searchIcon: {
        position: 'absolute',
        left: '0.75rem',
        top: '50%',
        transform: 'translateY(-50%)',
        width: '1.25rem',
        height: '1.25rem',
        color: colors.gray400,
    },
    inputBase: {
        width: '100%',
        paddingLeft: '2.5rem',
        paddingRight: '1rem',
        paddingTop: '0.625rem',
        paddingBottom: '0.625rem',
        border: `1px solid ${colors.gray300}`,
        borderRadius: '0.5rem',
        color: colors.gray900,
        backgroundColor: '#FFFFFF',
        outline: 'none',
        fontSize: '1rem',
        transition: 'box-shadow 0.15s ease-in-out, border-color 0.15s ease-in-out',
    },
    currentValueNote: {
        fontSize: '0.875rem',
        color: colors.gray600,
        paddingLeft: '0.5rem',
    },

    // States
    loadingState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '12.5rem',
        color: colors.blue600,
        flexDirection: 'column',
        gap: '0.5rem',
    },
    loaderIcon: {
        width: '2rem',
        height: '2rem',
        animation: 'spin 1s linear infinite',
    },
    loadingText: { fontSize: '1rem', fontWeight: 500 },
    errorState: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '12.5rem',
        color: colors.red600,
        fontWeight: 500,
        backgroundColor: colors.red50,
        borderRadius: '0.5rem',
        padding: '1rem',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100%',
        minHeight: '12.5rem',
        color: colors.gray500,
        gap: '0.5rem',
    },
    emptyIcon: { width: '2.5rem', height: '2.5rem' },
    emptyText: { fontSize: '1rem', fontWeight: 500 },

    // List
    listWrapper: {
        overflowY: 'auto',
        flexGrow: 1,
        borderRadius: '0.5rem',
        border: `1px solid ${colors.gray200}`,
        backgroundColor: colors.gray50,
    },
    listItem: {
        display: 'flex',
        alignItems: 'center',
        padding: '0.875rem 1rem',
        cursor: 'pointer',
        borderBottom: `1px solid ${colors.gray100}`,
        transition: 'background-color 0.15s ease',
    },
    listItemIcon: {
        width: '1.25rem',
        height: '1.25rem',
        marginRight: '0.75rem',
        color: colors.gray500,
        flexShrink: 0,
    },
    listItemText: {
        fontSize: '0.95rem',
        color: colors.gray900,
        fontWeight: 500,
    },

    // Buttons
    buttonSecondary: {
        padding: '0.625rem 1.25rem',
        border: `1px solid ${colors.gray300}`,
        borderRadius: '0.5rem',
        backgroundColor: '#fff',
        color: colors.gray700,
        cursor: 'pointer',
        fontWeight: 500,
        fontSize: '0.95rem',
        transition: 'all 0.15s ease-in-out',
    },
};

export default LesseePickerModal;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LesseePickerModal.tsx